import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { SentFileItemProps } from '../../types/message';

/**
 * 送信済みファイル表示コンポーネント
 * チャットメッセージに添付されたファイルを表示
 */
export const SentFileItem: React.FC<SentFileItemProps> = ({
  file,
  onDownload,
}) => {
  const theme = useTheme();

  // ファイルサイズをフォーマット
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // ファイル種類に応じたアイコンを取得
  const getFileIcon = (): React.ReactElement => {
    if (file.fileType.startsWith('image/')) {
      return <ImageIcon sx={{ color: theme.palette.primary.main }} />;
    }
    if (file.fileType === 'application/pdf') {
      return <PdfIcon sx={{ color: theme.palette.error.main }} />;
    }
    return <FileIcon sx={{ color: theme.palette.grey[600] }} />;
  };

  // ダウンロード処理
  const handleDownload = () => {
    if (onDownload) {
      onDownload(file);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1),
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.shape.borderRadius * 0.5,
        border: `1px solid ${theme.palette.grey[200]}`,
        gap: 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: theme.palette.grey[100],
          borderColor: theme.palette.grey[300],
        },
        ...(theme.palette.mode === 'dark' && {
          backgroundColor: theme.palette.grey[800],
          borderColor: theme.palette.grey[700],
          '&:hover': {
            backgroundColor: theme.palette.grey[700],
            borderColor: theme.palette.grey[600],
          },
        }),
      }}
    >
      {/* ファイルアイコン */}
      <Box sx={{ minWidth: 24, display: 'flex', justifyContent: 'center' }}>
        {getFileIcon()}
      </Box>

      {/* ファイル情報 */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.875rem',
          }}
        >
          {file.fileName}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.grey[600],
            fontSize: '0.75rem',
            display: 'block',
          }}
        >
          {formatFileSize(file.fileSize)}
        </Typography>
      </Box>

      {/* ダウンロードボタン */}
      {onDownload && (
        <Tooltip title="ダウンロード">
          <IconButton
            size="small"
            onClick={handleDownload}
            sx={{
              color: theme.palette.grey[600],
              '&:hover': {
                backgroundColor: theme.palette.primary.main + '20',
                color: theme.palette.primary.main,
              },
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}; 